import React from 'react';
import { Wrench } from 'lucide-react';
import { typography, palette } from '../../../../utils/designTokens';

/**
 * Shared renderValue - Renders a value recursively as structured JSX.
 * Handles null, string, boolean, number, arrays (inline if short, expanded if long), objects (nested key:value).
 */
const renderValue = (value) => {
  if (value === null || value === undefined) return <span>null</span>;
  if (typeof value === 'string') return <span>{value}</span>;
  if (typeof value === 'boolean') return <span>{value ? 'true' : 'false'}</span>;
  if (typeof value === 'number') return <span>{String(value)}</span>;
  if (Array.isArray(value)) {
    const json = JSON.stringify(value);
    if (json.length <= 60) return <span>{json}</span>;
    return (
      <div className="space-y-0.5 pl-2 mt-0.5" style={{ borderLeft: `1px solid ${palette.borders.subtle}` }}>
        {value.map((item, i) => (
          <div key={i}>{renderValue(item)}</div>
        ))}
      </div>
    );
  }
  if (typeof value === 'object') {
    return (
      <div className="space-y-0.5 pl-2 mt-0.5" style={{ borderLeft: `1px solid ${palette.borders.subtle}` }}>
        {Object.entries(value).map(([k, v]) => (
          <div key={k} className="flex gap-2">
            <span className="font-mono flex-shrink-0" style={{ color: palette.text.muted }}>{k}:</span>
            <div className="font-mono break-words" style={{ color: palette.text.primary, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {renderValue(v)}
            </div>
          </div>
        ))}
      </div>
    );
  }
  return <span>{String(value)}</span>;
};

/**
 * ToolCardHeader - Shared header with wrench icon + tool name + optional meta fields
 */
const ToolCardHeader = ({ name }) => (
  <div
    className="flex items-center gap-1.5 px-2.5 py-1.5 border-b"
    style={{ borderColor: palette.borders.subtle }}
  >
    <Wrench size={12} style={{ color: palette.accents.warm.base, flexShrink: 0 }} />
    <span
      className="font-mono font-semibold"
      style={{ fontSize: typography.size.xs, color: palette.accents.warm.base }}
    >
      {name}
    </span>
  </div>
);

/**
 * ToolCardBody - Shared body that renders data as key:value pairs or plain text
 */
const ToolCardBody = ({ data, fallbackText }) => (
  <div className="px-2.5 py-1.5" style={{ fontSize: typography.size.xs }}>
    {data && typeof data === 'object' && !Array.isArray(data) ? (
      <div className="space-y-1">
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="flex gap-2">
            <span className="font-mono flex-shrink-0" style={{ color: palette.text.muted }}>
              {key}:
            </span>
            <div
              className="font-mono break-words"
              style={{ color: palette.text.primary, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {renderValue(value)}
            </div>
          </div>
        ))}
      </div>
    ) : data !== null && data !== undefined ? (
      <div
        className="font-mono break-words"
        style={{ color: palette.text.primary, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
      >
        {renderValue(data)}
      </div>
    ) : (
      <div className="font-mono" style={{ color: palette.text.muted }}>
        {fallbackText || 'No data'}
      </div>
    )}
  </div>
);

/**
 * ToolCard - Displays a tool call or tool input as a structured card.
 * Used for LLM output tool calls and tool span inputs.
 *
 * @param {string} name - Tool name
 * @param {Object|string|null} data - Parsed arguments/parameters (caller handles parsing)
 */
const ToolCard = ({ name, data }) => (
  <div
    className="rounded border"
    style={{ borderColor: palette.borders.subtle, backgroundColor: palette.neutrals.canvas }}
  >
    <ToolCardHeader name={name} />
    <ToolCardBody data={data} fallbackText="No arguments" />
  </div>
);

/**
 * ToolResultCard - Displays a tool result/output as a structured card.
 * Extracts meaningful results from common output patterns (response.result, output, result).
 *
 * @param {string} name - Tool name
 * @param {Object|string|null} data - Parsed output data (caller handles parsing)
 */
const ToolResultCard = ({ name, data }) => {
  let resultContent = null;
  let metaFields = [];

  if (data && typeof data === 'object' && !Array.isArray(data)) {
    // Extract result from common patterns
    const response = data.response || data.output || data.result;
    if (response && typeof response === 'object' && typeof response.result === 'string') {
      resultContent = response.result;
    } else if (response && typeof response === 'object' && response.result && typeof response.result === 'object') {
      resultContent = response.result.text || response.result;
    } else if (typeof response === 'string') {
      resultContent = response;
    } else if (typeof data.result === 'string') {
      resultContent = data.result;
    } else if (response && typeof response === 'object') {
      resultContent = response;
    }

    // Collect meta fields (skip id, name, and result-related keys)
    const skipKeys = new Set(['response', 'output', 'result', 'name', 'id']);
    Object.entries(data).forEach(([key, value]) => {
      if (!skipKeys.has(key) && (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')) {
        metaFields.push({ key, value: String(value) });
      }
    });
  }

  return (
    <div
      className="rounded border"
      style={{ borderColor: palette.borders.subtle, backgroundColor: palette.neutrals.canvas }}
    >
      <ToolCardHeader name={name} />
      <div className="px-2.5 py-1.5 overflow-auto" style={{ fontSize: typography.size.xs, maxHeight: '400px' }}>
        {resultContent ? (
          <div
            className="font-mono break-words"
            style={{ color: palette.text.primary, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {typeof resultContent === 'string' ? resultContent : renderValue(resultContent)}
          </div>
        ) : data && typeof data === 'object' ? (
          <div className="font-mono break-words" style={{ color: palette.text.primary }}>
            {renderValue(data)}
          </div>
        ) : (
          <div
            className="font-mono break-words"
            style={{ color: palette.text.primary, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {typeof data === 'string' ? data : 'No output'}
          </div>
        )}
        {metaFields.length > 0 && (
          <div className="mt-2 pt-1.5 border-t flex flex-wrap gap-x-3 gap-y-0.5" style={{ borderColor: palette.borders.subtle }}>
            {metaFields.map(({ key, value }) => (
              <span key={key} className="font-mono" style={{ fontSize: typography.size.xs, color: palette.text.muted }}>
                {key}: {value}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Parse a value that may be double-encoded JSON.
 * @param {*} value - Raw value (string or object)
 * @returns {*} Parsed value
 */
const parseDoubleEncoded = (value) => {
  if (!value) return null;
  try {
    let parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (typeof parsed === 'string') {
      try { parsed = JSON.parse(parsed); } catch { /* keep as string */ }
    }
    return parsed;
  } catch {
    return value;
  }
};

export { ToolCard, ToolResultCard, renderValue, parseDoubleEncoded };
export default ToolCard;
